'use client';

import { Highlight } from '@/components/ui';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Funnel, MagnifyingGlass, TerminalWindow } from './icons';
import { fuzzyRank } from '@/lib/fuzzy';
import { shortId } from '@/lib/short-id';
import { criteriaToParams, type Criterion } from '@/domain/search-criteria';
import { quickOpenTargets, searchItemsFiltered } from '@/app/actions';
import type { FilteredHit } from '@/read';
import { glyphOf } from '@/domain/tags';
import {
  ATTRIBUTE_LABEL,
  suggestFilters,
  type SuggestContext,
  type Suggestion,
} from './search/filter-suggest';
import { CriterionChip, CriterionWords } from './search/criterion-chip';
import { PaletteCommandsContext, type CommandCtx, type PaletteCommand } from './palette-commands';
import { m, type PlainKey } from '@/i18n';
import { useT } from '@/lib/locale-context';
import styles from './quick-switcher.module.css';

/**
 * The command / quick-switcher `overlay` affordance (docs/MOBILE.md §2.4): one
 * palette, two modes.
 *
 * **Open mode** (Ctrl/Cmd+O, Ctrl/Cmd+K): one search box over three tiers,
 * deliberately separate engines (docs/SEARCH.md):
 * - **Places** (nav-pages, boards, map views, groups from the shell; flows,
 *   plans, ceremonies lazily fetched on first open; people from the client
 *   directory): the client fuzzy tier (`fuzzyRank`) - instant once loaded.
 *   Every row leads with its kind icon and wears a kind badge plus a muted
 *   context line (a view's scope, a flow's owner), so three things named
 *   "Tesserano" are tellable apart at a glance. A person "opens" as the
 *   assignee-filtered /search (there is no person page yet).
 * - **Narrow-by** (the map's criteria grammar, `filter-suggest.ts`): typing
 *   suggests attributes and values; picks become criterion chips worn in the
 *   input row, which the item tier then honours.
 * - **Items** (work-item content): the server tier (`searchItemsFiltered` ->
 *   the gated criteria search), FTS + trigram AND the chips, debounced
 *   as-you-type; a final "See all results" row jumps to /search carrying the
 *   same query + chips.
 *
 * **Command mode** (Ctrl/Cmd+P, or typing `>` first): the shell-assembled
 * command registry (`palette-commands.tsx`), fuzzy-ranked over label +
 * keywords; Backspace on an empty input returns to open mode.
 *
 * Place targets are plain hrefs to nav-pages, so jumping clears the trail; an
 * item hit opens the item at its plain URL (a jump, not a drilldown - the
 * trail starts fresh, per the accumulation rule).
 *
 * Escape ownership (the overlay-stacking rule, ui-kit.md): the palette can sit
 * above the route-intercept Modal peek, whose Escape handler listens at
 * `document` and defers only to a native `dialog[open]`. The palette is not a
 * native dialog, so it claims Escape with a NATIVE listener on its own panel
 * (stopPropagation runs before the document handlers) - the sheet.tsx pattern.
 * Escape first backs out of a pending attribute, then closes.
 */
export interface SwitcherTarget {
  href: string;
  label: string;
  kind: 'page' | 'board' | 'view' | 'lens' | 'group' | 'flow' | 'plan' | 'ceremony' | 'person';
  icon: React.ReactNode;
  /** Muted disambiguation beside the label (a view's scope, an owner). */
  context?: string;
}

export type PaletteMode = 'open' | 'command';

/** The kind badge's wording ('view' reads better spelled out), as catalog keys. */
const KIND_LABEL: Record<SwitcherTarget['kind'], PlainKey> = {
  page: 'palette.kind.page',
  board: 'palette.kind.board',
  view: 'palette.kind.view',
  lens: 'palette.kind.lens',
  group: 'palette.kind.group',
  flow: 'palette.kind.flow',
  plan: 'palette.kind.plan',
  ceremony: 'palette.kind.ceremony',
  person: 'palette.kind.person',
};

/** The search page's path, which the see-all row names as its kind. */
const SEARCH_PATH = '/search';

/** The open-mode result list, flattened so arrow keys walk one index space. */
type Row =
  | { key: string; type: 'place'; target: SwitcherTarget; positions: number[] }
  | { key: string; type: 'suggest'; s: Suggestion }
  | { key: string; type: 'hit'; hit: FilteredHit }
  | { key: string; type: 'seeAll' };

const SEARCH_DEBOUNCE_MS = 180;
const MAX_SUGGESTIONS = 4;

interface LazyTargets {
  flows: { id: string; name: string }[];
  plans: { id: string; name: string }[];
  ceremonies: { id: string; title: string }[];
}

export function QuickSwitcher({
  open,
  mode,
  onOpenChange,
  targets,
  commands,
  suggestCtx,
  lazyIcons,
  seedQuery,
}: {
  open: boolean;
  mode: PaletteMode;
  onOpenChange: (open: boolean, mode?: PaletteMode) => void;
  targets: SwitcherTarget[];
  commands: PaletteCommand[];
  /** Groups + people catalogs for the narrow-by suggestions. */
  suggestCtx: SuggestContext;
  /** Text to seed the query with when the palette opens (docs/VIEWS.md §12's
   *  Refine escalation hands off its unmatched text here). Consumed on the
   *  open transition only; empty/undefined opens fresh, as `Ctrl+O` does. */
  seedQuery?: string;
  /** Icons for the lazily-fetched target kinds (the shell owns the icon set). */
  lazyIcons: {
    flow: React.ReactNode;
    plan: React.ReactNode;
    ceremony: React.ReactNode;
    person: React.ReactNode;
  };
}) {
  const t = useT();
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [pending, setPending] = useState<Criterion['field'] | null>(null);
  const [hits, setHits] = useState<FilteredHit[]>([]);
  const [lazy, setLazy] = useState<LazyTargets | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Monotonic ticket so a slow earlier search can never clobber a newer one.
  const searchSeqRef = useRef(0);

  // Context-sensitive commands, contributed by the current route's surface
  // (empty in v1 - the extension seam, see palette-commands.tsx).
  const contextCommands = useContext(PaletteCommandsContext);

  // The lazy target catalog (flows/plans/ceremonies): one gated fetch on the
  // first open per shell mount, cached for the session.
  useEffect(() => {
    if (!open || mode !== 'open' || lazy !== null) return;
    let cancelled = false;
    quickOpenTargets().then(
      (t) => {
        if (!cancelled) setLazy(t);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [open, mode, lazy]);

  const allTargets = useMemo<SwitcherTarget[]>(() => {
    const extra: SwitcherTarget[] = [];
    if (lazy) {
      for (const f of lazy.flows) {
        // The short-id disambiguator (lib/short-id.ts): same-named flows
        // tell apart without an owner riding the name.
        extra.push({
          href: `/flows/${f.id}`,
          label: f.name,
          kind: 'flow',
          icon: lazyIcons.flow,
          context: shortId(f.id),
        });
      }
      for (const p of lazy.plans) {
        extra.push({ href: `/plans/${p.id}`, label: p.name, kind: 'plan', icon: lazyIcons.plan });
      }
      for (const c of lazy.ceremonies) {
        extra.push({
          href: `/ceremonies/${c.id}`,
          label: c.title,
          kind: 'ceremony',
          icon: lazyIcons.ceremony,
        });
      }
    }
    // People come from the suggest catalog (the client directory): "opening" a
    // person lands on their assignee-filtered /search.
    for (const a of suggestCtx.actors) {
      extra.push({
        href: `/search?assignee=${encodeURIComponent(a.ref)}`,
        label: a.name,
        kind: 'person',
        icon: lazyIcons.person,
      });
    }
    return [...targets, ...extra];
  }, [targets, lazy, suggestCtx, lazyIcons]);

  const commandHits = useMemo(
    () =>
      mode === 'command'
        ? fuzzyRank(query, [...commands, ...contextCommands], (c) =>
            [t.msg(c.label), ...(c.keywords ? [t.msg(c.keywords)] : [])].join(' '),
          )
        : [],
    [mode, query, commands, contextCommands, t],
  );

  // Gate at render, not by clearing state in the effect: without text or chips
  // the content tier simply does not apply (stale fetches are fenced by seq).
  const q = query.trim();
  const searching = q.length >= 2 || criteria.length > 0;
  const rows = useMemo<Row[]>(() => {
    if (mode !== 'open') return [];
    // Value mode: the pending attribute's values are the whole list.
    if (pending) {
      return suggestFilters(query, pending, suggestCtx, t).map(
        (s): Row => ({ key: `s:${JSON.stringify(s)}`, type: 'suggest', s }),
      );
    }
    const out: Row[] = fuzzyRank(query, allTargets, (t) => t.label).map(
      (r): Row => ({
        key: `p:${r.item.kind}:${r.item.href}`,
        type: 'place',
        target: r.item,
        positions: r.positions,
      }),
    );
    if (q.length > 0) {
      for (const s of suggestFilters(query, null, suggestCtx, t).slice(0, MAX_SUGGESTIONS)) {
        out.push({ key: `s:${JSON.stringify(s)}`, type: 'suggest', s });
      }
    }
    if (searching) {
      for (const hit of hits) out.push({ key: `h:${hit.id}`, type: 'hit', hit });
      out.push({ key: 'see-all', type: 'seeAll' });
    }
    return out;
  }, [mode, pending, query, q, suggestCtx, allTargets, searching, hits, t]);

  const total = mode === 'command' ? commandHits.length : rows.length;
  const activeIndex = Math.min(active, Math.max(0, total - 1));
  const activeId = total > 0 ? `qs-option-${activeIndex}` : undefined;

  // The global hotkeys mount with the shell (this component is always
  // rendered): Ctrl/Cmd+O and Ctrl/Cmd+K open quick-open, Ctrl/Cmd+P command
  // mode. preventDefault claims them from the browser (open-file / print);
  // Ctrl+K stays as the established alias.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      const key = e.key.toLowerCase();
      if (key === 'k' || key === 'o') {
        e.preventDefault();
        onOpenChange(true, 'open');
      } else if (key === 'p') {
        e.preventDefault();
        onOpenChange(true, 'command');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onOpenChange]);

  // Open: lock scroll and move focus in; close: give focus back. (The query
  // resets in `close`, so every open starts fresh without setState in an effect.)
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    // Seed on open (docs/VIEWS.md §12): a Refine escalation opens the palette
    // with its unmatched text already in the box. Captured at the open
    // transition (deps [open]) so a seed prop change while open never
    // overwrites what the viewer is typing; `Ctrl+O` opens seedless.
    if (seedQuery) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sanctioned sync-on-open seed (query + roving index)
      setQuery(seedQuery);
      setActive(0);
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      returnFocusRef.current?.focus();
    };
    // seedQuery is captured at open only (intentional stale closure); re-seeding
    // mid-session would clobber the viewer's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The content tier, debounced: text AND the chips, searched server-side
  // through the gated criteria search.
  useEffect(() => {
    if (!open || mode !== 'open') return;
    const text = query.trim();
    const seq = ++searchSeqRef.current;
    if (text.length < 2 && criteria.length === 0) return;
    const timer = setTimeout(() => {
      searchItemsFiltered({
        q: text.length >= 2 ? text : '',
        criteria,
        op: 'all',
        limit: 8,
      }).then(
        (page) => {
          if (searchSeqRef.current === seq) setHits(page.hits);
        },
        () => {
          if (searchSeqRef.current === seq) setHits([]);
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, mode, query, criteria]);

  // Keep the active option in view while arrowing through an overflowing list.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const close = () => {
    onOpenChange(false);
    setQuery('');
    setActive(0);
    setCriteria([]);
    setPending(null);
    setHits([]);
  };
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  });

  // Escape ownership: a NATIVE listener on the panel claims Escape before any
  // document-level handler (the Modal peek's) can also act on it (see the
  // component doc). A pending attribute backs out first; then Escape closes.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (pendingRef.current) {
        setPending(null);
        setQuery('');
        setActive(0);
      } else {
        closeRef.current();
      }
    };
    panel.addEventListener('keydown', onKey);
    return () => panel.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const go = (href: string) => {
    close();
    router.push(href);
  };
  const commandCtx: CommandCtx = { push: go, close };
  const pickSuggestion = (s: Suggestion) => {
    if (s.kind === 'attribute') {
      setPending(s.field);
      setQuery('');
      setActive(0);
      inputRef.current?.focus();
      return;
    }
    setCriteria((c) => [...c, s.criterion]);
    setQuery('');
    setPending(null);
    setActive(0);
    inputRef.current?.focus();
  };
  const seeAllHref = () => `/search?${criteriaToParams(criteria, 'all', q).toString()}`;
  const goTo = (index: number) => {
    if (mode === 'command') {
      const cmd = commandHits[index];
      if (cmd) cmd.item.run(commandCtx);
      return;
    }
    const row = rows[index];
    if (!row) return;
    if (row.type === 'place') return go(row.target.href);
    if (row.type === 'suggest') return pickSuggestion(row.s);
    // eslint-disable-next-line no-restricted-syntax -- nav/quick-jump: intentionally clears trail
    if (row.type === 'hit') return go(`/items/${row.hit.id}`);
    return go(seeAllHref());
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (total === 0 ? 0 : (a + 1) % total));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (total === 0 ? 0 : (a - 1 + total) % total));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (total > 0) goTo(activeIndex);
    } else if (e.key === 'Backspace' && query === '') {
      if (mode === 'command') {
        // The `>` comes off: back to open mode (the token-field convention).
        e.preventDefault();
        onOpenChange(true, 'open');
        setActive(0);
      } else if (pending) {
        e.preventDefault();
        setPending(null);
      } else if (criteria.length > 0) {
        e.preventDefault();
        setCriteria((c) => c.slice(0, -1));
      }
    } else if (e.key === 'Tab') {
      // The input is the only tab stop; keep focus inside the dialog.
      e.preventDefault();
    }
  };

  const onQueryChange = (value: string) => {
    // `>` as the first character switches to command mode (the palette
    // convention); the prefix itself never becomes query text.
    if (mode === 'open' && value.startsWith('>') && !pending) {
      onOpenChange(true, 'command');
      setQuery(value.slice(1).trimStart());
      setActive(0);
      return;
    }
    setQuery(value);
    setActive(0);
  };

  const sectionBreak = (i: number): boolean => {
    // A hairline where the row type changes (places -> narrow-by -> items).
    const prev = rows[i - 1];
    const cur = rows[i];
    return Boolean(prev && cur && prev.type !== cur.type);
  };

  return (
    <div className={styles.scrim} onClick={close} data-testid="quick-switcher-scrim">
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'command' ? t('palette.panel.command') : t('palette.panel.open')}
        onClick={(e) => e.stopPropagation()}
        data-testid="quick-switcher"
        data-mode={mode}
      >
        <div className={styles.inputRow}>
          {mode === 'command' ? (
            <TerminalWindow className={styles.searchIcon} aria-hidden />
          ) : (
            <MagnifyingGlass className={styles.searchIcon} aria-hidden />
          )}
          {mode === 'command' && (
            <span className={styles.modeMark} aria-hidden>
              &gt;
            </span>
          )}
          {mode === 'open' &&
            // The criteria bar's own chip, so a filter reads the same on both
            // surfaces (the accent "not", the hairline, the faint "or"); whole
            // in the input row, where the input gives way.
            criteria.map((c, i) => (
              <CriterionChip
                key={i}
                criterion={c}
                suggestCtx={suggestCtx}
                onRemove={() => setCriteria((cs) => cs.filter((_, j) => j !== i))}
                shrink={false}
                testId="qs-chip"
              />
            ))}
          {mode === 'open' && pending && (
            <span className={styles.modeMark}>
              {t('criteria.bar.pending', { attribute: m(ATTRIBUTE_LABEL[pending]) })}
            </span>
          )}
          {/* eslint-disable-next-line no-restricted-syntax -- bespoke combobox search: chromeless input inside the bordered panel with a leading icon and combobox ARIA, like entity-picker's own search; the Input primitive's control chrome would fight the container. */}
          <input
            ref={inputRef}
            className={styles.input}
            role="combobox"
            aria-expanded="true"
            aria-controls="qs-listbox"
            aria-activedescendant={activeId}
            aria-label={mode === 'command' ? t('palette.input.command') : t('palette.input.open')}
            placeholder={
              mode === 'command'
                ? t('palette.input.commandPlaceholder')
                : t('palette.input.openPlaceholder')
            }
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        {total === 0 ? (
          <p className={styles.empty}>
            {mode === 'command'
              ? t('palette.empty.command')
              : searching
                ? t('palette.empty.noMatches')
                : t('palette.empty.hint')}
          </p>
        ) : (
          <ul id="qs-listbox" ref={listRef} className={styles.list} role="listbox">
            {mode === 'command'
              ? commandHits.map((r, i) => (
                  <li
                    key={r.item.id}
                    id={`qs-option-${i}`}
                    className={styles.option}
                    role="option"
                    aria-selected={i === activeIndex}
                    data-active={i === activeIndex || undefined}
                    onPointerMove={() => setActive(i)}
                    onClick={() => r.item.run(commandCtx)}
                    data-testid="qs-command"
                  >
                    <span className={styles.optIcon}>{r.item.icon}</span>
                    <span className={styles.optLabel}>{t.msg(r.item.label)}</span>
                    <span className={styles.kind}>{t('palette.kind.command')}</span>
                  </li>
                ))
              : rows.map((row, i) => (
                  <QuickRow
                    key={row.key}
                    row={row}
                    index={i}
                    active={i === activeIndex}
                    withBreak={sectionBreak(i)}
                    onHover={() => setActive(i)}
                    onPick={() => goTo(i)}
                  />
                ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** One open-mode result row: place / narrow-by suggestion / item hit / see-all. */
function QuickRow({
  row,
  index,
  active,
  withBreak,
  onHover,
  onPick,
}: {
  row: Row;
  index: number;
  active: boolean;
  withBreak: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const t = useT();
  const li = (children: React.ReactNode, testId?: string, closed?: boolean) => (
    <>
      {withBreak && <li className={styles.section} aria-hidden />}
      <li
        id={`qs-option-${index}`}
        className={styles.option}
        role="option"
        aria-selected={active}
        data-active={active || undefined}
        data-closed={closed ? 'true' : undefined}
        onPointerMove={onHover}
        onClick={onPick}
        data-testid={testId}
      >
        {children}
      </li>
    </>
  );
  if (row.type === 'place') {
    return li(
      <>
        <span className={styles.optIcon}>{row.target.icon}</span>
        <span className={styles.optLabel}>
          <Highlight text={row.target.label} positions={row.positions} />
          {row.target.context && <span className={styles.context}>{row.target.context}</span>}
        </span>
        <span className={styles.kind}>{t(KIND_LABEL[row.target.kind])}</span>
      </>,
      'qs-place',
    );
  }
  if (row.type === 'suggest') {
    const s = row.s;
    return li(
      s.kind === 'attribute' ? (
        <>
          <span className={styles.optIcon}>
            <Funnel aria-hidden />
          </span>
          <span className={styles.optLabel}>{s.label}</span>
          <span className={styles.kind}>{t('palette.kind.narrowBy')}</span>
        </>
      ) : (
        <>
          <span className={styles.optIcon}>
            <Funnel aria-hidden />
          </span>
          <span className={styles.optLabel}>
            {s.display ? <CriterionWords message={s.display} /> : s.label}
            <span className={styles.context}>{s.attribute}</span>
          </span>
          <span className={styles.kind}>{t('palette.kind.filter')}</span>
        </>
      ),
      'qs-suggest',
    );
  }
  if (row.type === 'hit') {
    const crumbs = row.hit.path.map((c) => c.title).join(' / ');
    const glyph = glyphOf(row.hit, row.hit.tags);
    return li(
      <>
        <span
          className={styles.hitDot}
          data-health={glyph.health}
          data-tense={glyph.tense}
          aria-hidden
        />
        <span className={styles.optLabel}>
          {row.hit.title}
          {crumbs && <span className={styles.context}>{crumbs}</span>}
        </span>
        <span className={styles.kind}>{t('palette.kind.item')}</span>
      </>,
      'qs-item-hit',
      glyph.closed,
    );
  }
  return li(
    <>
      <span className={styles.optIcon}>
        <MagnifyingGlass aria-hidden />
      </span>
      <span className={styles.optLabel}>{t('palette.row.seeAll')}</span>
      <span className={styles.kind}>{SEARCH_PATH}</span>
    </>,
    'qs-see-all',
  );
}
